import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { ReportRequest } from '@/store/slices/reportingV2/reportingV2.api';
import {
  type ReportingV2Filters,
  type WidgetState,
  fetchReportWidget,
  selectReportingV2Filters,
  selectReportingV2Freshness,
  selectReportingV2Widget,
} from '@/store/slices/reportingV2/reportingV2Slice';
import { useEffect } from 'react';
import { daysStale } from '../format';
import { STALE_DAYS_THRESHOLD } from '../lib/freshnessMessage';
import { ribeyeFilterKeys } from '../widgets';

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/**
 * Metrics fetched for the prior window. Must cover every KPI strip tile, or that
 * tile alone reads "no prior data" while its neighbours show a delta — which has
 * now happened twice (#4109 vcr, #4367 frequency). Pinned by
 * kpiPriorPeriodParity.test.ts against the strip's own tile list.
 *
 * cpm is absent by design: it is derived front-end from spend and impressions.
 * client_cpm is NOT derived the same way (#4446) — it's the line item's own
 * sold CPM, not spend ÷ impressions, so it has to be fetched like client_spend.
 */
export const PRIOR_PERIOD_METRICS = [
  'impressions',
  'spend',
  'clicks',
  'reach',
  'ctr',
  'vcr',
  'frequency',
  'client_spend',
  'client_cpm',
];

/** Shift a [start, end] window back by the same duration to get the prior period. */
export const shiftBack = (start: string, end: string): { start: string; end: string } => {
  const s = new Date(`${start}T00:00:00`);
  const e = new Date(`${end}T00:00:00`);
  const days = Math.round((e.getTime() - s.getTime()) / 86_400_000) + 1;
  const priorEnd = new Date(s);
  priorEnd.setDate(priorEnd.getDate() - 1);
  const priorStart = new Date(priorEnd);
  priorStart.setDate(priorStart.getDate() - (days - 1));
  return { start: ymd(priorStart), end: ymd(priorEnd) };
};

/**
 * Clip a requested window's end date to the freshest-complete day shared by the
 * in-scope platforms, the same cutoff the "reported through N days ago" banner
 * already shows (flank#4689 preprod QA: Ivan). Without this, a window that runs
 * past that cutoff (e.g. "Last 30 Days" extending 3 days past the actual data)
 * compares a current period padded with near-zero incomplete days against a
 * fully-elapsed prior period, producing deltas that read as a steep decline
 * when nothing actually changed.
 *
 * A single chronically-behind platform (more than STALE_DAYS_THRESHOLD days
 * stale — the same bar the freshness banner uses) is excluded from the cutoff
 * computation rather than allowed to veto every other platform's data.
 * freshnessMessage.ts already carved this exact case out for the banner
 * (flank#4586: one DSP 23 days behind rendered as "reported through 23 days
 * ago" for everyone, with eight others current to 2 days). flank#4710 ported
 * that carve-out here, but only for the unfiltered ("no dsps selected")
 * branch — an EXPLICIT multi-platform selection still took the single oldest
 * date across every selected platform, so selecting a chronically-stale
 * platform (e.g. Meta, dormant for weeks) alongside a fresh one (DV360) still
 * let Meta veto DV360's own near-complete data. Confirmed live on preprod
 * (flank#4718 follow-up): with Meta + DV360 selected, Meta's weeks-old
 * freshness date clipped the cutoff to before `dateStart` entirely, which
 * tripped `comparisonWindowEnd`'s invert guard below and fell back to the
 * raw, unclamped `dateEnd` — silently undoing the #4722 clamp for both the
 * clamped-current and prior-period fetches whenever a chronically-stale
 * platform rode along with a fresh one.
 *
 * The carve-out now applies to any multi-platform scope, explicit or not.
 * A platform the user explicitly filters to BY ITSELF still clamps to its own
 * freshness even when chronically stale (flank#4710), since they asked for
 * exactly that one — filtering it out would leave nothing in scope, and the
 * fallback below restores it in that case.
 */
export const effectiveEndDate = (
  dateEnd: string,
  dsps: string[],
  freshness: Record<string, string | undefined>,
): string => {
  const scopedDsps = dsps.length > 0 ? dsps : Object.keys(freshness);
  const nonChronic = scopedDsps.filter(dsp => (daysStale(freshness[dsp]) ?? 0) <= STALE_DAYS_THRESHOLD);
  const effectiveScope = nonChronic.length > 0 ? nonChronic : scopedDsps;
  const cutoff = effectiveScope
    .map(dsp => freshness[dsp])
    .filter((date): date is string => Boolean(date))
    .sort()[0];
  return cutoff && cutoff < dateEnd ? cutoff : dateEnd;
};

/** Compute a percentage delta between current and prior values. Returns null if prior is 0. */
export const pctDelta = (current: unknown, prior: unknown): number | null => {
  const c = Number(current ?? 0);
  const p = Number(prior ?? 0);
  if (!p) return null;
  return ((c - p) / p) * 100;
};

/**
 * The prior-period window for a given current-period request, freshness-clamped
 * like `effectiveEndDate`.
 *
 * A short window (e.g. "Last 7 Days") can have less slack than the most stale
 * in-scope platform's lag, so `effectiveEndDate` alone can land before
 * `dateStart`. Feeding that straight into `shiftBack` produces a negative-length
 * window that comes out inverted (priorStart after priorEnd), which the backend
 * 422s — failing the 'totalsPrior' fetch and reading as "no prior data" on every
 * KPI tile even though real prior-period data exists.
 *
 * Flooring the clamp at `dateStart` (an earlier version of this fix) avoids the
 * 422 but creates a worse bug (review, Lucas): it shrinks the PRIOR window to a
 * sliver while the CURRENT period stays full-length, so a stale platform makes
 * every tile's delta read as a wild, meaningless swing (e.g. +500%) instead of
 * "no prior data" — misleading rather than merely incomplete. Falling back to
 * the unclamped `dateEnd` instead keeps the prior window the same length as the
 * current period, which is what a period-over-period comparison requires;
 * ClientRecapCard's own narrative-text clamp (#4704) is a different, unrelated
 * consumer and is untouched by this change.
 *
 * That invariant only held for the prior window in isolation: the CURRENT
 * period's own 'totals' widget (fetched in widgets.ts's `buildRequest`, read
 * by KpiStrip/KpiCompareTable) never applied this same clamp, so whenever the
 * clamp had slack but didn't invert, the current period stayed at its full
 * requested length while the prior window clamped shorter — unequal windows,
 * inflating every KPI delta (flank#4718, confirmed live: a real 7-day DV360
 * comparison read as +40.7% instead of +22%). `comparisonWindowEnd` below is
 * the shared cutoff both `priorPeriodWindow` and `useClampedCurrentTotals`
 * build from, so the two can no longer drift apart.
 */
export const comparisonWindowEnd = (
  dateStart: string,
  dateEnd: string,
  dsps: string[],
  freshness: Record<string, string | undefined>,
): string => {
  const rawClippedDateEnd = effectiveEndDate(dateEnd, dsps, freshness);
  return rawClippedDateEnd >= dateStart ? rawClippedDateEnd : dateEnd;
};

export const priorPeriodWindow = (
  dateStart: string,
  dateEnd: string,
  dsps: string[],
  freshness: Record<string, string | undefined>,
): { start: string; end: string } => shiftBack(dateStart, comparisonWindowEnd(dateStart, dateEnd, dsps, freshness));

/**
 * Request body shared by the prior-period and clamped-current comparison
 * fetches below — identical dimensions/metrics/scoping filters, only the date
 * range differs. Factored out so a scoping filter can't reach one fetch and
 * not the other the way whitelabelIds/clientIds once did (#4454).
 *
 * ctr is included so the CTR card/row gets a prior-period baseline like its
 * neighbours (cpm is derived FE-side from spend/impressions). Keep in sync
 * with the KPI strip's tiles. `vcr` was missing after VCR replaced Reach on
 * the strip (#4109), so that tile alone read "no prior data" forever.
 * `frequency` went the same way when it was added (#4367) — hence the parity
 * test in kpiPriorPeriodParity.test.ts, so the next metric added to the strip
 * fails the suite instead of silently reading "no prior data". `reach` stays
 * — the compare table still shows it.
 */
const buildComparisonRequest = (
  filters: ReportingV2Filters,
  dateRange: { start: string; end: string },
): ReportRequest => ({
  dimensions: [],
  metrics: PRIOR_PERIOD_METRICS,
  date_range: dateRange,
  ...(filters.dsps.length ? { dsps: filters.dsps } : {}),
  // whitelabelIds/clientIds were missing here (#4454) — every other scoping
  // filter on the bar reached the prior-period comparison except these two,
  // so a White Label or Client selection changed the current-period numbers
  // but not the delta they were compared against.
  ...(filters.whitelabelIds.length ? { whitelabel_ids: filters.whitelabelIds } : {}),
  ...(filters.clientIds.length ? { client_ids: filters.clientIds } : {}),
  ...(filters.advertiserIds.length ? { advertiser_ids: filters.advertiserIds } : {}),
  ...ribeyeFilterKeys(filters),
  ...(filters.productIds.length ? { product_ids: filters.productIds } : {}),
  ...(filters.publishers.length ? { publishers: filters.publishers } : {}),
  ...(filters.channelIds.length ? { channel_ids: filters.channelIds } : {}),
  ...(filters.deviceIds.length ? { device_ids: filters.deviceIds } : {}),
  pagination: { limit: 1, offset: 0 },
});

/**
 * Fetches the same totals widget for the prior period window and exposes its rows.
 * The key 'totalsPrior' avoids stomping the live 'totals' widget state.
 */
export const usePriorPeriodTotals = (): WidgetState => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectReportingV2Filters);
  const freshness = useAppSelector(selectReportingV2Freshness);
  const prior = useAppSelector(selectReportingV2Widget('totalsPrior'));

  const { start, end } = priorPeriodWindow(filters.dateStart, filters.dateEnd, filters.dsps, freshness);

  useEffect(() => {
    dispatch(
      fetchReportWidget({
        key: 'totalsPrior',
        request: buildComparisonRequest(filters, { start, end }),
      }),
    );
  }, [
    dispatch,
    filters.dateStart,
    start,
    end,
    filters.dsps.join(),
    filters.whitelabelIds.join(),
    filters.clientIds.join(),
    filters.advertiserIds.join(),
    filters.ribeyeCampaignIds.join(),
    filters.ribeyeLineItemIds.join(),
    filters.productIds.join(),
    filters.publishers.join(),
    filters.channelIds.join(),
    filters.deviceIds.join(),
  ]);

  return prior;
};

/**
 * Fetches the CURRENT period's totals under the same freshness-clamped end
 * date used for `priorPeriodWindow` above, so the KPI comparison always diffs
 * equal-length windows (flank#4718). This is a second, comparison-only fetch
 * under its own key ('totalsCurrentClamped') — the page's headline numbers
 * (KpiStrip's big figure, KpiCompareTable's "current" column) keep reading
 * the main 'totals' widget (widgets.ts, raw/unclamped filter dates). The
 * freshness banner elsewhere on the page already discloses when that figure
 * runs past the last complete day, so only the delta next to it needs to be
 * apples-to-apples; changing what the headline figure itself displays would
 * be a bigger, unasked-for behavior change.
 */
export const useClampedCurrentTotals = (): WidgetState => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectReportingV2Filters);
  const freshness = useAppSelector(selectReportingV2Freshness);
  const current = useAppSelector(selectReportingV2Widget('totalsCurrentClamped'));

  const clippedDateEnd = comparisonWindowEnd(filters.dateStart, filters.dateEnd, filters.dsps, freshness);

  useEffect(() => {
    dispatch(
      fetchReportWidget({
        key: 'totalsCurrentClamped',
        request: buildComparisonRequest(filters, { start: filters.dateStart, end: clippedDateEnd }),
      }),
    );
  }, [
    dispatch,
    filters.dateStart,
    clippedDateEnd,
    filters.dsps.join(),
    filters.whitelabelIds.join(),
    filters.clientIds.join(),
    filters.advertiserIds.join(),
    filters.ribeyeCampaignIds.join(),
    filters.ribeyeLineItemIds.join(),
    filters.productIds.join(),
    filters.publishers.join(),
    filters.channelIds.join(),
    filters.deviceIds.join(),
  ]);

  return current;
};
